<template>
    <div class="content-container">
        <div class="content h-auto">
            <div class="container">
                <div class="row">

                    <!-- Title -->
                    <div class="col-12">
                        <div class="title">
                            <h1>


                              <template v-if="requiresPreBooking">{{$t('bp.TheCheckout.preBookingTitle')}}</template>
                              <template v-else-if="inWarrantyStatus && ! warrantyRepairChargeable">{{$t('bp.TheCheckout.notWarrantyRepairChargeableTitle')}}</template>
                              <template v-else-if="inWarrantyStatus && warrantyRepairChargeable">{{$t('bp.TheCheckout.warrantyRepairChargeableTitle')}}</template>
                              <template v-else>{{$t('bp.TheCheckout.title')}}</template>
                            </h1>
                        </div>
                    </div>

                    <!-- Repair Details -->
                    <div class="col-12">
                        <div class="card">
                            <div class="card-header">
                                <h4>
                                  <template v-if="bookingIsDropOff">{{$t('bp.TheCheckout.location.dropOff')}}</template>
                                  <template v-else>{{$t('bp.TheCheckout.location.doorstep')}}</template>
                                </h4>
                                <router-link v-if="allowsStoreDropOff" :to="{name: 'repairOptions'}">Change</router-link>
                            </div>
                            <div class="card-body pb-2">
                                <dl>
                                    <dt>{{$t('bp.TheCheckout.location')}}</dt>
                                    <dd>{{ parseAddressToString(booking.address) }}</dd>
                                </dl>
                                <dl>
                                    <dt>{{$t('bp.TheCheckout.date')}}</dt>
                                    <dd>{{ booking.full_date }}</dd>
                                </dl>
                                <dl v-if="!bookingIsDropOff">
                                    <dt>{{$t('bp.TheCheckout.timeslot')}}</dt>
                                    <dd>{{$t('bp.TheCheckout.timeslot.text')}}</dd>
                                </dl>
                            </div>
                        </div>
                    </div>

                    <!-- Basket -->
                    <div class="col-12">
                        <div class="card">
                            <div class="card-header">
                                <h4>{{$t('bp.TheCheckout.basket')}}</h4>
                                <router-link :to="{name: 'basket'}" :disabled="confirmingUnpaidBooking">{{$t('bp.TheCheckout.editBasketButton')}}</router-link>
                            </div>
                            <div class="card-body">
                                <basket-item
                                        v-for="(device, key) in devices"
                                        :key="key"
                                        :device-data="device"
                                        :device-key="key"
                                        :is-promo="isPromoEvent"
                                        hideControls>
                                </basket-item>
                            </div>
                        </div>
                    </div>

                    <!-- Promo Code -->
                    <div class="col-12" v-if="!inWarrantyStatus || warrantyRepairChargeable">
                        <div class="card collapsable-card"
                             :class="{ 'active' : promoCodeInputOpen }">
                            <div class="card-body">
                                <div class="card-header p-0">
                                    <h4>
                                        <template v-if="!codeAdded">{{$t('bp.TheCheckout.promoCode')}}</template>
                                        <template v-if="codeAdded">{{ code }}{{$t('bp.TheCheckout.promo.applied')}}<span><i class="fa fa-check text-base text-success ml-2"></i></span></template>
                                    </h4>
                                    <a href="#" v-if="!codeAdded && !promoCodeInputOpen" @click="showPromoCodeInput()">{{$t('bp.TheCheckout.promoCode.addButton')}}</a>
                                    <a href="#" v-if="!codeAdded && promoCodeInputOpen" @click="hidePromoCodeInput()">{{$t('bp.TheCheckout.promo.cancel')}}</a>
                                    <a href="#" @click="triggerRemovePromoCode()" v-if="codeAdded">
                                        <span v-if="!removingCode">{{$t('bp.TheCheckout.promo.remove')}}</span>
                                        <span v-if="removingCode">{{$t('bp.TheCheckout.promo.removing')}}<i class="fa fa-spinner fa-pulse"></i></span>
                                    </a>
                                </div>
                                <div class="collapse" :class="{ 'show': promoCodeInputOpen }">
                                    <div class="collapse-body p-0 pt-3">
                                        <div class="collapse-content">
                                            <div class="form-group form-inline promo-code-input">
                                                <input type="text" class="form-control" :placeholder="$t('bp.TheCheckout.promo.placeholder')" v-model="code" :disabled="addingCode">
                                                <button class="btn btn-lg continue-btn" @click="triggerAddPromoCode()" v-if="!addingCode">
                                                  {{$t('bp.TheCheckout.promo.add')}}</button>
                                                <button class="btn btn-lg continue-btn" v-if="addingCode" disabled><span><i class="fa fa-spinner fa-pulse mr-2"></i></span> Adding...</button>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div class="col-12" v-if="!isPromoEvent && (!inWarrantyStatus || warrantyRepairChargeable)">
                        <!-- Total Cost -->
                        <table class="price-table grand-total">
                            <template v-if="discountCodeInCart">
                                <tr>
                                    <td>Subtotal:</td>
                                    <td><span v-html='currencySymbol'></span>{{ fullPrice }}</td>
                                </tr>
                                <tr>
                                    <td>Promo:</td>
                                    <td>-<span v-html='currencySymbol'></span>{{ discountTotal }}</td>
                                </tr>
                            </template>
                            <tr class="total-cost">
                                <td>{{$t('bp.total')}}</td>
                                <td><span v-html='currencySymbol'></span>{{ cartTotal }}</td>
                            </tr>
                        </table>
                    </div>

                    <div class="col-12" v-if="isPromoEvent">
                        <!-- Promo Booking -->
                        <div class="card">
                            <div class="card-header">
                                <h4>Promotional Event</h4>
                            </div>
                            <div class="card-body">
                                <dl class="mb-0">
                                    <dt>
                                        {{ booking.promotion_event.name }}
                                    </dt>
                                    <dd v-if="booking.promotion_event.description">
                                        {{ booking.promotion_event.description }}
                                    </dd>
                                </dl>
                            </div>
                        </div>
                    </div>

                    <div class="col-12 text-center">
                        <p class="mb-4 text-sm" v-if="inWarrantyStatus">
                          {{$t('bp.TheCheckout.inWarranty.message')}}
                        </p>
                        <p class="mb-4 text-sm">
                            {{$t('bp.TheCheckout.policy.p1')}} <router-link :to="{name: 'Terms'}" target="_blank">{{$t('bp.policy.p2')}}</router-link> {{$t('bp.policy.p3')}} <router-link :to="{name: 'Privacy'}" target="_blank">{{$t('bp.privacyPolicy')}}</router-link><template v-if="countryCodeSetting === 'DE'">{{$t('bp.policy.p4')}}</template>
                        </p>
                    </div>

                    <template v-if="requiresPreBooking">
                        <div class="col-12 d-flex">
                            <router-link :to="{ name: 'inStoreBookingConfirmation' }" class="btn btn-success btn-lg continue-btn icon-right ml-auto mb-4">Create Pre-booking <i class="fa fa-chevron-right ml-3 text-sm"></i></router-link>
                        </div>
                    </template>

                    <template v-else>
                        <div class="col-12 d-flex" v-if="isPromoEvent || (inWarrantyStatus && !warrantyRepairChargeable)">
                            <!-- In Warranty & Promo Booking confirm footer -->
                            <button @click="triggerConfirmUnpaidBooking" class="btn btn-success btn-lg continue-btn icon-right ml-auto mb-4" :disabled="confirmingUnpaidBooking">{{ completeBookingText }}</button>
                        </div>

                        <div class="col-12 d-flex" v-if="!isPromoEvent && (!inWarrantyStatus || warrantyRepairChargeable)">
                            <!-- OOW proceed to payment footer -->
                            <router-link :to="{ name: 'paymentOptions' }" class="btn btn-success btn-lg continue-btn icon-right ml-auto mb-4">{{$t('bp.TheCheckout.proceedButton')}}<i class="fa fa-chevron-right ml-3 text-sm"></i></router-link>
                        </div>
                    </template>

                    <!-- T&Cs and Protection -->
                    <div class="col-12 d-flex text-center">
                        <div class="col-4">
                            <img srcset="https://res.cloudinary.com/wefix/image/upload/v1564755328/booking/norton_logo.png 1x, https://res.cloudinary.com/wefix/image/upload/v1564755328/booking/norton_logo_2x.png 2x"
                                 src="https://res.cloudinary.com/wefix/image/upload/v1564755328/booking/norton_logo.png"
                                 alt="Norton Secured"
                                 class="protection-logo" />
                        </div>
                        <div class="col-4">
                            <img srcset="https://res.cloudinary.com/wefix/image/upload/v1564755425/booking/etrust_logo.png 1x, https://res.cloudinary.com/wefix/image/upload/v1564755425/booking/etrust_logo_2x.png 2x"
                                 src="https://res.cloudinary.com/wefix/image/upload/v1564755425/booking/etrust_logo.png"
                                 alt="TRUSTe Verified"
                                 class="protection-logo" />
                        </div>
                        <div class="col-4">
                            <img srcset="https://res.cloudinary.com/wefix/image/upload/v1564755484/booking/mcafee_logo.png 1x, https://res.cloudinary.com/wefix/image/upload/v1564755484/booking/mcafee_logo_2x.png 2x"
                                 src="https://res.cloudinary.com/wefix/image/upload/v1564755484/booking/mcafee_logo.png"
                                 alt="McAfee Secure"
                                 class="protection-logo" />
                        </div>
                    </div>

                </div>

                <!-- Out of Warranty Modal -->
                <out-of-warranty-modal></out-of-warranty-modal>

            </div>
        </div>
    </div>
</template>

<script>
    import {mapActions, mapGetters} from "vuex";

    export default {
        name: 'Checkout',

        mounted() {
            this.checkCanBeOnPage();

            if (this.discountCodeInCart) {
                this.code = this.discountCodeInCart;
                this.codeAdded = true;
            }
        },

        data() {
           return {
               addingCode: false,
               code: null,
               codeAdded: false,
               confirmingUnpaidBooking: false,
               promoCodeInputOpen: false,
               removingCode: false
           }
        },

        computed: {
            ...mapGetters([
                'allowsStoreDropOff',
                'booking',
                'carrierDetails',
                'cartTotal',
                'devices',
                'discountCodeInCart',
                'discountTotal',
                'fullPrice',
                'inWarrantyStatus',
                'parseAddressToString',
                'repairLocationOption',
                'selectedProblems',
                'wasBookedInStore',
                'warrantyRepairChargeable',
                'isBookingInStore',
                'bookingIsDropOff',
                'currencySymbol',
                'countryCodeSetting'
            ]),

            locationType() {
                return (this.bookingIsDropOff) ? 'In-store drop-off' : 'Doorstep repair';
            },

            pageTitle() {
                if (this.requiresPreBooking) {
                    return "Check the details below and proceed to pre-booking";
                }

                if (this.inWarrantyStatus && !this.warrantyRepairChargeable) {
                    return "Check your details below and confirm booking";
                }

                if (!this.inWarrantyStatus || this.warrantyRepairChargeable) {
                    return "Check your details below and proceed to payment";
                }

                return "Check your details below and proceed";
            },

            requiresPreBooking() {
                return (this.isBookingInStore && (!this.inWarrantyStatus || this.warrantyRepairChargeable));
            },

            completeBookingText() {
                if (this.confirmingUnpaidBooking) {
                    return 'Completing booking...';
                }

                return 'Complete booking';
            },

            isPromoEvent() {
                return this.booking.is_promotion_event;
            }
        },

        methods: {
            ...mapActions([
                'addPromoCode',
                'checkBookingIsComplete',
                'checkDeviceWarrantyDetails',
                'confirmWarrantyBooking',
                'confirmPromoBooking',
                'displayToast',
                'removePromoCode'
            ]),

            checkCanBeOnPage() {
                this.checkBookingIsComplete().then(() => {
                    if (!this.selectedProblems.length) {
                        return this.$router.push({name: 'repairSelect'})
                    }

                    this.checkDeviceWarrantyDetails();

                    if (!this.booking.date) {
                        this.$router.push({name: 'repairDates'});
                    }
                });
            },

            hidePromoCodeInput() {
                this.promoCodeInputOpen = false;
            },

            showPromoCodeInput() {
                this.promoCodeInputOpen = true;
            },

            triggerAddPromoCode() {
                if (this.addingCode) {
                    return;
                }

                if (this.inWarrantyStatus && !this.warrantyRepairChargeable) {
                    this.displayToast({text: this.$t('bp.Toast.promo.warrantyError'), type: 'error'});
                    return;
                }

                this.addingCode = true;
                this.codeAdded = false;

                this.addPromoCode(this.code).then(() => {
                    this.addingCode = false;
                    this.codeAdded = true;
                    this.hidePromoCodeInput();

                }).catch(error => {
                    this.addingCode = false;

                    if (error.response.data.code === "CA05") {
                        this.displayToast({text:this.$t('bp.Toast.promo.notRecognised') , type: "error"});

                    } else if (error.response.data.code === "CA06") {
                        this.displayToast({text:this.$t('bp.Toast.promo.used') , type: "error"});
                    }
                });
            },

            triggerRemovePromoCode() {
                if (this.removingCode) {
                    return;
                }

                this.removingCode = true;

                this.removePromoCode().then(() => {
                    this.code = null;
                    this.codeAdded = false;
                    this.promoCodeInputOpen = false;
                    this.removingCode = false;
                }).catch(() => {
                    this.removingCode = false;
                });
            },

            triggerConfirmUnpaidBooking() {
                if (this.confirmingUnpaidBooking) return;

                this.confirmingUnpaidBooking = true;

                if (this.inWarrantyStatus) {
                    return this.triggerConfirmWarrantyBooking();

                } else {
                    return this.triggerConfirmPromotionBooking();
                }
            },

            triggerConfirmWarrantyBooking() {
                this.confirmWarrantyBooking().then(() => {
                    this.confirmingUnpaidBooking = false;
                    this.$router.push({name: 'bookingComplete'});
                }).catch(error => {
                    this.confirmingUnpaidBooking = false;

                   if (error.response.data.code === 'RBK15') {
                       return this.$bvModal.show("oow-modal");
                   }

                    if (error.response.data.code === 'BK23') {
                        this.displayToast({text: this.$t('bp.Toast.promo.multiDevice'), type: "error"});
                        this.$router.push({name: 'basket'});
                    }

                    if (error.response.data.code === 'BK26') {
                        this.$router.push({name: 'paymentOptions'});
                    }
                });
            },

            triggerConfirmPromotionBooking() {
                this.confirmPromoBooking().then(() => {
                    this.confirmingUnpaidBooking = false;
                    this.$router.push({name: 'bookingComplete'});
                }).catch(() => {
                    this.confirmingUnpaidBooking = false;

                    this.displayToast({text: this.$t('bp.Toast.promo.notValid'), type: "error"});
                });
            }
        }
    }
</script>

<style scoped>

</style>